import React from 'react';
import { Redirect } from 'react-router';

import {
  PartnersTableSortableField,
  PartnersTableItem,
  PageSize,
} from 'types/state';
import { useServices } from 'hooks/useServices';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { usePartnersPageMode } from 'hooks/usePartnersPageMode';
import { useServiceState } from 'hooks/useServiceState';
import { routes } from 'utils/routes';
import { Pagination } from 'design/pagination';
import { testIdMap } from 'utils/testIdMap';
import { AdminContentLayout } from 'design/adminContentLayout';
import { AdminPageTitleLayout } from 'design/adminPageTitleLayout';
import { AdminPageHeadLayout } from 'design/adminPageHeadLayout';
import { PageSizeControl } from 'containers/PageSizeControl';
import { useTableSortingController } from 'hooks/useTableSortingController';
import { TableControlsLayout } from 'design/tableControlsLayout';
import { PartnersTable } from 'containers/PartnersTable';
import { UnreachableError } from 'utils/errors';
import { PartnerManager } from 'containers/PartnerManager/PartnerManager';
import { usePartnerManager } from 'hooks/usePartnerManager';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { ClientDetails } from 'containers/ClientDetails';
import { TextComponent } from 'design/text/textComponents';
import { SearchInputLayoutComponent } from 'design/searchInputLayout';
import { TextInputControlComponent } from 'design/textInputControl';
import { AdminNotificationPanel } from 'containers/AdminNotificationPanel';

type Props = {
  currentPage: number;
};

const DEFAULT_SORT_FIELD: PartnersTableSortableField = 'id';
const DEFAULT_SORT_ASCENDING_FLAG = false;
const MIN_SEARCH_VALUE_LENGTH_TO_SEARCH_BY = 2;

export const PartnersPageContent = (props: Props) => {
  const { currentPage } = props;

  const dispatch = useAppDispatcher();

  const mode = usePartnersPageMode();

  const { sortBy, tableSorting: sorting } = useTableSortingController({
    initialField: DEFAULT_SORT_FIELD,
    initialAscending: DEFAULT_SORT_ASCENDING_FLAG,
  });

  const { partnersPage: ownDictionary } = useIntlDictionary();

  const { partnerId: partnerManageToId } = usePartnerManager();

  // Флаг отвечает за появление и скрытие модального окна
  // с подробностями о клиенте.
  const [
    clientIdToShowDetailsFor,
    setClientIdToShowDetailsFor,
  ] = React.useState<null | number>(null);

  const closeClientDetails = React.useCallback(() => {
    setClientIdToShowDetailsFor(null);
  }, []);

  // Состояние хранит количество страниц, доступных для
  // загрузки. Состояние хранится отдельно, чтобы пагинация
  // не пропадала при каждом новом вызове сервиса.
  const [totalPagesCount, setTotalPagesCount] = React.useState<null | number>(
    null
  );

  // Состояние хранит текстовое значение по которому будет
  // производиться поиск клиентов.
  const [searchValue, setSearchValue] = React.useState('');

  // Для осуществления поиска по текстовому значению подходит
  // только строка определенной длины. Чтобы не провоцоировать
  // лишний вызов сервиса на получение данных страницы, значения
  // поисковой строки меньше определенной длины приводятся к
  // одинаковому значению.
  const searchBy =
    searchValue.length < MIN_SEARCH_VALUE_LENGTH_TO_SEARCH_BY
      ? null
      : searchValue;

  // Обработчик изменившегося значения поисковой строки.
  const onSearchValueChange = setSearchValue;

  // Состояние хранит значение размера страницы.
  const [sizeValue, setSizeValue] = React.useState<PageSize>(20);

  // Обработчик изменившегося значения размера страницы.
  const onSizeValueChange = setSizeValue;

  // Подключение сервисов.
  const { getPartnersPageData } = useServices();

  // Состояние и метод вызова сервиса на получение данных страницы.
  const [pageDataState, getPageDataByParams] = useServiceState(
    getPartnersPageData
  );

  const getPageData = React.useCallback(() => {
    const params = { page: currentPage, pageSize: sizeValue, mode, sorting };
    if (searchBy) {
      getPageDataByParams({ ...params, searchBy });
    } else {
      getPageDataByParams(params);
    }
  }, [currentPage, getPageDataByParams, mode, searchBy, sizeValue, sorting]);

  // Эффект вызывает сервис на получение данных страницы после
  // монтирования и каждый раз, когда изменится один из параметров.
  // Используется useLayoutEfeect чтобы избежать мерцания индикатора
  // сортировки.
  React.useLayoutEffect(() => {
    getPageData();
  }, [getPageData]);

  // Эффект следит за состоянием сервиса на получение данных страницы
  // и обновляет значение доступного количества страниц.
  // Используется useLayoutEffect чтобы в случае несуществующей
  // страницы осуществить редирект без отрисовки пустых данных.
  React.useLayoutEffect(() => {
    if (pageDataState && pageDataState.resolvedWith) {
      setTotalPagesCount(pageDataState.resolvedWith.totalPagesCount);
    }
  }, [pageDataState]);

  // Список клиентов кэшируется в локальном состоянии.
  const [tableItems, setTableItems] = React.useState<
    null | PartnersTableItem[]
  >(null);

  // Кэшированный список клиентов инвалидируется при смене страницы.
  React.useEffect(() => {
    setTableItems(null);
  }, []);

  // При получении нового списка клиентов он заменяет собой кэшированный список.
  React.useEffect(() => {
    const tableItems =
      pageDataState &&
      pageDataState.resolvedWith &&
      pageDataState.resolvedWith.partners;

    if (tableItems) {
      setTableItems(tableItems);
    }
  }, [pageDataState]);

  // Обработчик нажатия по строке таблицы.
  // Показывает подробности о клиенте.
  const handleTableItemClick = React.useCallback(
    (id: number) => {
      dispatch({
        name: 'PartnersPage__TableItemClicked',
        partnerId: id,
      });

      getPageData();
    },
    [dispatch, getPageData]
  );

  const showClientDetails = React.useCallback(
    (clientId: number) => {
      dispatch({
        name: 'PartnersPage__ClientDetailsWillBeShown',
      });
      setClientIdToShowDetailsFor(clientId);
    },
    [dispatch]
  );

  // В случае, когда выбранная страница не существует,
  // осуществляется редирект на последнюю доступную страницу.
  // Ситуация возникает, например, когда переключается размер страницы.
  if (totalPagesCount !== null && currentPage > totalPagesCount) {
    if (totalPagesCount > 0) {
      if (mode === 'ACTUAL_PARTNERS') {
        return <Redirect to={routes.partners(totalPagesCount)} />;
      }
    } else if (currentPage > 1) {
      if (mode === 'ACTUAL_PARTNERS') {
        return <Redirect to={routes.partners(1)} />;
      }
    }
  }

  const paginationBasePath = (() => {
    switch (mode) {
      case 'ACTUAL_PARTNERS':
        return routes.partnersRoot();
      default:
        throw new UnreachableError(mode);
    }
  })();

  return (
    <AdminContentLayout
      headNode={
        <AdminPageHeadLayout>
          <AdminNotificationPanel />
          <AdminPageTitleLayout
            textNode={
              <TextComponent size="mp">{ownDictionary.titleText}</TextComponent>
            }
            actionNode={null}
          />
          <TableControlsLayout
            primaryControlsNode={
              <TextComponent size="s">
                <SearchInputLayoutComponent
                  control={
                    <TextInputControlComponent
                      placeholder={ownDictionary.searchInputPlaceholderText}
                      value={searchValue}
                      onValueChange={onSearchValueChange}
                      testId={testIdMap.PARTNERS_PAGE__SEARCH}
                    />
                  }
                />
              </TextComponent>
            }
            secondaryControlsNode={
              <>
                {totalPagesCount !== null && (
                  <TextComponent size="s">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPagesCount}
                      basePath={paginationBasePath}
                    />
                  </TextComponent>
                )}
                <TextComponent size="s">
                  <PageSizeControl
                    value={sizeValue}
                    onValueChange={onSizeValueChange}
                  />
                </TextComponent>
              </>
            }
          ></TableControlsLayout>
        </AdminPageHeadLayout>
      }
      bodyNode={
        <>
          {tableItems !== null && (
            <PartnersTable
              items={tableItems}
              activeItemId={partnerManageToId || undefined}
              sortedBy={sorting.field}
              ascendingSort={sorting.ascending}
              onSortChange={sortBy}
              onItemClick={handleTableItemClick}
            />
          )}
          {partnerManageToId !== null && (
            <PartnerManager
              partnerId={partnerManageToId}
              onDataEdited={getPageData}
              onViewClientDetails={showClientDetails}
            />
          )}
          {clientIdToShowDetailsFor !== null && (
            <ClientDetails
              clientId={clientIdToShowDetailsFor}
              onClose={closeClientDetails}
              onLoginEdited={closeClientDetails}
              onPasswordEdited={closeClientDetails}
              onProfileEdited={closeClientDetails}
              onStatusEdited={closeClientDetails}
              onDebitPayed={closeClientDetails}
            />
          )}
        </>
      }
    />
  );
};
